import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, ArrowLeft, Search, BookOpen, Play, MessageCircle, FileText, Video, HelpCircle, Phone, Mail, AlertTriangle, Lightbulb, Shield, ExternalLink, Heart, X, User, MessageSquare } from 'lucide-react';
import { checkAuth, logout } from '../auth/config';
import NavigationCarousel from '../components/NavigationCarousel';
import { useTheme } from '../contexts/ThemeContext';
import DarkModeToggle from '../components/DarkModeToggle';

const HelpSupport = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  // Check for signup user first, then demo user as fallback
  const signupUser = JSON.parse(localStorage.getItem('user') || 'null');
  const demoUser = checkAuth();
  const user = signupUser || demoUser;
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    category: '',
    subject: '',
    message: ''
  });
  const [showSecurityReport, setShowSecurityReport] = useState(false);
  const [securityReport, setSecurityReport] = useState({
    subject: '',
    message: ''
  });

  const toggleSidebar = () => setIsSidebarCollapsed(prev => !prev);

  const sidebarMenuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  const handleLogout = () => {
    logout();
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('onboardingComplete');
    window.location.href = '/login';
  };

  const handleBack = () => {
    navigate('/app/profile');
  };

  const handleSubmitRequest = () => {
    if (!selectedCategory) {
      alert('Please select a category first');
      return;
    }
    setContactForm(prev => ({ ...prev, category: selectedCategory }));
    setShowContactForm(true);
  };

  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSendSupport = () => {
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      alert('Please fill in both subject and message');
      return;
    }

    const subject = encodeURIComponent(`[${contactForm.category}] ${contactForm.subject}`);
    const body = encodeURIComponent(
      `Category: ${contactForm.category}\n\n` +
      `Subject: ${contactForm.subject}\n\n` +
      `Message:\n${contactForm.message}\n\n` +
      `---\n` +
      `User: ${user?.name || 'Unknown'}\n` +
      `Email: ${user?.email || 'Unknown'}\n` +
      `Submitted: ${new Date().toLocaleString()}`
    );
    const mailtoLink = `mailto:support@axora.app?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
    setContactForm({ category: '', subject: '', message: '' });
    setShowContactForm(false);
    setSelectedCategory('');
  };

  const handleSaveDraft = () => {
    const drafts = JSON.parse(localStorage.getItem('supportDrafts') || '[]');
    const draft = { id: Date.now(), ...contactForm, createdAt: new Date().toISOString() };
    drafts.push(draft);
    localStorage.setItem('supportDrafts', JSON.stringify(drafts));
    alert('Draft saved successfully!');
    setShowContactForm(false);
  };

  const handleCancelForm = () => {
    setShowContactForm(false);
    setContactForm({ category: '', subject: '', message: '' });
  };

  const handleSecurityReportChange = (field, value) => {
    setSecurityReport(prev => ({ ...prev, [field]: value }));
  };

  const handleSendSecurityReport = () => {
    if (!securityReport.subject.trim() || !securityReport.message.trim()) {
      alert('Please fill in both subject and message');
      return;
    }
    alert('Security report submitted. Thank you!');
    setShowSecurityReport(false);
    setSecurityReport({ subject: '', message: '' });
  };

  const handleNavigationClick = (item) => {
    if (item.isLogout) { handleLogout(); return; }
    navigate(item.path);
  };

  const supportCategories = [
    'Technical Issue',
    'Account or Login',
    'Payment or Billing',
    'Content or Course Issue',
    'Feedback or Suggestions'
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Sidebar Navigation */}
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={sidebarMenuItems}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        {/* Header */}
        <div className="bg-[#AC5757] rounded-l-lg" style={{ marginLeft: '10px', transition: 'margin-left 0.3s ease' }}>
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <button onClick={handleBack} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <ArrowLeft size={20} className="text-white" />
              </button>
              <h1 className="font-oswald font-medium text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[65px]">HELP & SUPPORT</h1>
            </div>
            <div className="flex items-center gap-3">
              <DarkModeToggle size="small" />
              <Link to="/app/messages" className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <MessageSquare size={20} className="text-white" />
              </Link>
              <Link to="/app/profile" className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <User size={20} className="text-white" />
              </Link>
              <button onClick={handleLogout} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <LogOut size={20} className="text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl px-6 py-8" style={{ marginLeft: '20px', transition: 'margin-left 0.3s ease' }}>
          {/* Back Button (kept inside content too) */}
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-[#AC5757] hover:text-[#8A4A4A] transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Profile</span>
          </button>

          {/* Page Title */}
          <div className="mb-8">
            <h2 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-3xl font-bold mb-2`}>Help & Support</h2>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>We’re here to help you succeed on Axora.</p>
          </div>

          {/* 1. Quick Help */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Search size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">1. Quick Help</h3>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Help Center */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Help Center</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Browse articles to learn features and best practices.</p>
                </div>

                {/* Getting Started Guide */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Play size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Getting Started Guide</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>New to Axora? Start here → <span className="text-[#AC5757] font-semibold cursor-pointer hover:underline">Onboarding Guide</span></p>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Walk through account setup, verification, and your first course in under five minutes.</p>
                </div>

                {/* Live Chat Assistant */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageCircle size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Live Chat Assistant</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Need help now? Chat with our AI-powered assistant available 24/7.</p>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>If your issue isn't resolved, we'll connect you to a human support agent within minutes.</p>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm text-gray-500 mt-2`}>Look for the chat icon in the bottom-right corner of your screen to start chatting with our AI assistant.</p>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Support Requests */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <FileText size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">2. Support Requests</h3>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Submit a Request */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Mail size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Submit a Request</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Use the form below to reach our team. Choose a category:</p>
                  <div className="space-y-2 mb-6">
                    {supportCategories.map((category, index) => (
                      <label key={index} className={`${isDarkMode ? 'bg-gray-700 border border-gray-600' : 'flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="category"
                          value={category}
                          checked={selectedCategory === category}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className={`${isDarkMode ? 'text-[#AC5757]' : 'text-[#AC5757]'}`}
                        />
                        <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{category}</span>
                      </label>
                    ))}
                  </div>
                  <button 
                    onClick={handleSubmitRequest}
                    className="px-6 py-3 bg-[#AC5757] text-white rounded-lg hover:bg-[#8A4A4A] transition-colors"
                  >
                    Submit → Contact Support
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Tutorials & Learning Assistance */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Video size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">3. Tutorials & Learning Assistance</h3>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Video Tutorials */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Play size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Video Tutorials</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Watch visual demos on how to:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Create and manage Studios</li>
                    <li>Upload and review designs in Pin-Up</li>
                    <li>Use VR/AR learning modules</li>
                    <li>Collaborate and share feedback</li>
                  </ul>
                  <button className="mt-4 px-6 py-3 bg-[#AC5757] text-white rounded-lg hover:bg-[#8A4A4A] transition-colors">
                    Watch Tutorials
                  </button>
                </div>

                {/* Interactive Walkthroughs */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <HelpCircle size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Interactive Walkthroughs</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>In-app guides appear as tooltips the first time you try a new feature.</p>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>You can replay them anytime under Settings → Tutorials.</p>
                </div>

                {/* FAQs */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <HelpCircle size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>FAQs</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Find fast answers about:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Subscription plans</li>
                    <li>Certificates and credentials</li>
                    <li>Payment methods</li>
                    <li>Data privacy and profile settings</li>
                  </ul>
                  <button 
                    onClick={() => navigate('/app/faqs')}
                    className="mt-4 px-6 py-3 border border-[#AC5757] text-[#AC5757] rounded-lg hover:bg-[#AC5757] hover:text-white transition-colors"
                  >
                    Browse FAQs
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* 4. Contact & Escalation */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Phone size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">4. Contact & Escalation</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Method</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900">Email Support</td>
                        <td className="py-3 px-4 text-gray-600">
                          <span className="text-[#AC5757] font-semibold">support@axora.app</span>
                          <br />
                          <span className="text-sm">— Expect a reply within 24–48 hours.</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900">Call Support (Premium Users)</td>
                        <td className="py-3 px-4 text-gray-600">
                          Schedule a callback or use our hotline for institutional clients.
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900">Report a Problem</td>
                        <td className="py-3 px-4 text-gray-600">
                          Use <span className="text-[#AC5757] font-semibold cursor-pointer hover:underline">Report an Issue</span> for bugs, broken pages, or content errors.
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-gray-900">Safety & Misconduct Reports</td>
                        <td className="py-3 px-4 text-gray-600">
                          Confidentially report harassment, plagiarism, or inappropriate content.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* 5. Feedback */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Lightbulb size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">5. Feedback</h3>
                </div>
              </div>
              <div className="p-6">
                {/* Feature Requests */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb size={20} className="text-[#AC5757]" />
                    <h4 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>Feature Requests</h4>
                  </div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Have an idea? Submit it through our Feature Request Portal and vote on other users' suggestions.</p>
                  <button className="px-6 py-3 border border-[#AC5757] text-[#AC5757] rounded-lg hover:bg-[#AC5757] hover:text-white transition-colors">
                    Submit Request
                  </button>
                </div>
              </div>
            </div>
          </section>


          {/* 6. Helpful Links */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <ExternalLink size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">6. Helpful Links</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button 
                    onClick={() => navigate('/app/privacy-security')}
                    className={`${isDarkMode ? 'flex items-center gap-3 p-4 border border-gray-600 rounded-lg hover:bg-gray-700' : 'flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'} text-left`}
                  >
                    <Shield size={20} className="text-[#AC5757]" />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Privacy & Security Settings</span>
                  </button>
                  <button className={`${isDarkMode ? 'flex items-center gap-3 p-4 border border-gray-600 rounded-lg hover:bg-gray-700' : 'flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'} text-left`}>
                    <FileText size={20} className="text-[#AC5757]" />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Terms of Service</span>
                  </button>
                  <button 
                    onClick={() => setShowSecurityReport(true)}
                    className={`${isDarkMode ? 'flex items-center gap-3 p-4 border border-gray-600 rounded-lg hover:bg-gray-700' : 'flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'} text-left`}
                  >
                    <AlertTriangle size={20} className="text-[#AC5757]" />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Report a Security Issue</span>
                  </button>
                  <button className={`${isDarkMode ? 'flex items-center gap-3 p-4 border border-gray-600 rounded-lg hover:bg-gray-700' : 'flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'} text-left`}>
                    <Shield size={20} className="text-[#AC5757]" />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>View Data Policy</span>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Our Promise */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gradient-to-r from-[#AC5757] to-[#8A4A4A]' : 'bg-gradient-to-r from-[#AC5757] to-[#8A4A4A]'} rounded-2xl p-8 text-white text-center`}>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Heart size={24} className="text-white" />
                <h3 className="text-2xl font-bold">Our Promise</h3>
              </div>
              <p className="text-lg leading-relaxed max-w-3xl mx-auto">
                We believe in responsive, transparent support. Whether it's a technical bug or a learning roadblock, 
                our mission is to ensure that every Axora user feels empowered, informed, and heard.
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-xl shadow-2xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-xl font-bold`}>Contact Support</h3>
              <button onClick={() => setShowContactForm(false)} className={`${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} w-8 h-8 rounded-full flex items-center justify-center`}>
                <X size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            <div className="space-y-3">
              <input className={`${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} w-full border rounded-lg px-4 py-3`} placeholder={`Subject (${selectedCategory || 'General'})`} value={contactForm.subject} onChange={e=>handleContactFormChange('subject',e.target.value)} />
              <textarea className={`${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} w-full border rounded-lg px-4 py-3 h-36`} placeholder="Describe your issue..." value={contactForm.message} onChange={e=>handleContactFormChange('message',e.target.value)} />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleCancelForm} className={`${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} flex-1 px-4 py-3 border rounded-lg`}>Cancel</button>
              <button onClick={handleSaveDraft} className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600">Save Draft</button>
              <button onClick={handleSendSupport} className="flex-1 px-4 py-3 bg-[#AC5757] text-white rounded-lg hover:bg-[#8A4A4A]">Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Security Report Modal */}
      {showSecurityReport && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-xl shadow-2xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-xl font-bold`}>Security Report</h3>
              <button onClick={() => setShowSecurityReport(false)} className={`${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} w-8 h-8 rounded-full flex items-center justify-center`}>
                <X size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            <div className="space-y-3">
              <input className={`${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} w-full border rounded-lg px-4 py-3`} placeholder="Subject" value={securityReport.subject} onChange={e=>handleSecurityReportChange('subject',e.target.value)} />
              <textarea className={`${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'} w-full border rounded-lg px-4 py-3 h-36`} placeholder="Describe the issue..." value={securityReport.message} onChange={e=>handleSecurityReportChange('message',e.target.value)} />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowSecurityReport(false)} className={`${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} flex-1 px-4 py-3 border rounded-lg`}>Cancel</button>
              <button onClick={handleSendSecurityReport} className="flex-1 px-4 py-3 bg-[#AC5757] text-white rounded-lg hover:bg-[#8A4A4A]">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSupport;

